import { ColDef } from 'ag-grid-community'

import { DATE_FILTER_PRESETS } from '@/components/lemmala-design-system/table/consts'
import type {
  DateFilterModel,
  DateFilterSelection,
} from '@/components/lemmala-design-system/table/types'
import {
  findDatePreset,
  formatModelDate,
  parseDate,
} from '@/components/lemmala-design-system/table/utils/date-range-filter'
import { TimeframeFilter } from '@/components/lemmala-design-system/timeframe-filter/timeframe-filter'
import {
  encodeCustomRange,
  parseCustomRange,
} from '@/components/lemmala-design-system/timeframe-filter/utils'

export interface AgDateFilterProps<TRowData extends object> {
  columnDef: ColDef<TRowData>
  selected?: DateFilterModel
  defaultOpen?: boolean
  onChange: (field: string, selection: DateFilterSelection) => void
  onClear: (field: string) => void
  onRemove?: () => void
  // Important to allow filter popovers to catch interactions, e.g. when used within a modal/dialog
  inModal?: boolean
}

const toTimeframeValue = (selected?: DateFilterModel) => {
  if (!selected) return ''
  if (selected.type !== 'inRange') return selected.type
  const from = parseDate(selected.dateFrom)
  const to = parseDate(selected.dateTo)
  return from && to ? encodeCustomRange({ from, to }) : ''
}

export const AgDateFilter = <TRowData extends object>({
  columnDef,
  selected,
  defaultOpen = false,
  onChange,
  onClear,
  onRemove,
  inModal = false,
}: AgDateFilterProps<TRowData>) => {
  const fieldKey = (columnDef.colId as string) || (columnDef.field as string)

  const handleChange = (value: string) => {
    const preset = findDatePreset(value)
    if (preset) {
      onChange(fieldKey, { preset: preset.value })
      return
    }
    const range = parseCustomRange(value)
    if (range) {
      onChange(fieldKey, {
        dateFrom: formatModelDate(range.from),
        dateTo: formatModelDate(range.to),
      })
    }
  }

  return (
    <TimeframeFilter
      label={columnDef.headerName ?? ''}
      presets={DATE_FILTER_PRESETS}
      value={toTimeframeValue(selected)}
      onChange={handleChange}
      allowCustomRange
      onClear={() => onClear(fieldKey)}
      onRemove={onRemove}
      defaultOpen={defaultOpen}
      modal={inModal}
    />
  )
}
